import { AttentionBlock, Checkbox, ChoiceRow, Radio, RadioGroup } from '@westhetics/ui'
import type { IntegrationsCatalog, Scope } from './api'
import { ACCESS_LABELS, accessOf, type Access } from './labels'

/**
 * As permissões do app (docs/areas/integracoes.md, "Escopos"; RN-INT-03): um segmento por recurso, "Não acessa ·
 * Ler · Ler e escrever" (o catálogo só lê), e as duas marcas à parte: as respostas da anamnese, que são dado de
 * saúde, e os webhooks pela API. Os rótulos e o que cada um libera vêm do catálogo da API.
 */

const errorClass =
  'font-body text-eyebrow text-plum mt-[6px] leading-[1.5] font-light tracking-normal'

function setAccess(
  scopes: readonly Scope[],
  resource: IntegrationsCatalog['resources'][number],
  access: Access,
): Scope[] {
  const others = scopes.filter((s) => s !== resource.read && s !== resource.write)
  if (access === 'none') return others
  if (access === 'read' || !resource.write) return [...others, resource.read]
  return [...others, resource.write]
}

function toggle(scopes: readonly Scope[], scope: Scope, on: boolean): Scope[] {
  const others = scopes.filter((s) => s !== scope)
  return on ? [...others, scope] : others
}

export function PermissionsFields({
  catalog,
  value,
  onChange,
  error,
  disabled,
}: {
  catalog: IntegrationsCatalog
  value: Scope[]
  onChange: (scopes: Scope[]) => void
  error?: string
  disabled?: boolean
}) {
  const describe = (scope: Scope) => catalog.scopes.find((s) => s.scope === scope)?.description
  const answers = value.includes('anamneses:answers')
  return (
    <div className="flex flex-col gap-5">
      {catalog.resources.map((resource) => {
        const access = accessOf(value, resource)
        const levels: Access[] = resource.write ? ['none', 'read', 'write'] : ['none', 'read']
        const scopeOfAccess =
          access === 'write' ? resource.write : access === 'read' ? resource.read : null
        return (
          <fieldset key={resource.resource} className="flex flex-col gap-2" disabled={disabled}>
            <legend className="font-display text-name text-ink mb-2 leading-[1.4] font-normal">
              {resource.label}
            </legend>
            <RadioGroup
              aria-label={resource.label}
              className="tablet:grid-cols-3"
              value={access}
              disabled={disabled}
              onValueChange={(next) => onChange(setAccess(value, resource, next as Access))}
            >
              {levels.map((level) => (
                <ChoiceRow key={level} control={<Radio value={level} />}>
                  {ACCESS_LABELS[level]}
                </ChoiceRow>
              ))}
            </RadioGroup>
            <p className="font-body text-secondary text-[12px] leading-[1.6] font-light">
              {scopeOfAccess
                ? describe(scopeOfAccess)
                : `O app não vê nada de ${resource.label.toLocaleLowerCase('pt-BR')}.`}
            </p>
          </fieldset>
        )
      })}

      <div className="border-hairline flex flex-col gap-2 border-t pt-5">
        <ChoiceRow
          control={
            <Checkbox
              checked={answers}
              disabled={disabled}
              onCheckedChange={(on) => onChange(toggle(value, 'anamneses:answers', on === true))}
            />
          }
          description={describe('anamneses:answers')}
        >
          Ler as respostas da anamnese
        </ChoiceRow>
        {answers ? (
          <AttentionBlock tone="pampas" title="Respostas da anamnese são dado de saúde">
            Marque só se o sistema que recebe é da clínica e guarda o prontuário com o mesmo
            cuidado. Cada leitura fica no registro de chamadas do app.
          </AttentionBlock>
        ) : null}
        <ChoiceRow
          control={
            <Checkbox
              checked={value.includes('webhooks:write')}
              disabled={disabled}
              onCheckedChange={(on) => onChange(toggle(value, 'webhooks:write', on === true))}
            />
          }
          description={describe('webhooks:write')}
        >
          Gerenciar os próprios webhooks pela API
        </ChoiceRow>
      </div>

      {error ? (
        <p role="alert" className={errorClass}>
          {error}
        </p>
      ) : null}
    </div>
  )
}
